/* eslint-disable */
import { yupResolver } from "@hookform/resolvers/yup";
import { useContext, useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import { LocalizationContext } from "../../../App";
import { cn, Formatters } from "../../../common/helpers/formatters";
import {
  CheckCircle,
  EmptyCircle,
  DocumentText,
  InfoCircle,
  ShieldCheck,
} from "../../../common/icons";
import { Modal } from "../../../common/Modal";
import { ProductContext } from "../../../context/ProductContext";
import { DeclarationType } from "../../../language/localization";
import { AmendPolicy } from "../../../models/amendPolicy";
import { SubmitInsuranceProductRequest } from "../../../models/request/submitInsuranceProductRequest";
import { setDIRates, setMonthlyPriceDi, setMonthlyPricePpi, setPPRates } from "../../../store/appSlice";
import { useCalculatePriceMutation } from "../../../store/policiesSlice";
import { RootState, useAppDispatch, useAppSelector } from "../../../store/store";
import ToggleButton from "../../common/ToggleButton";

const declarationSchema = yup.object().shape({
  aboveIsTruthful: yup.boolean().required().oneOf([true]),
});

type PaymentFrequency = "y" | "q";

type PolicyAmendmentProps = {
  details: AmendPolicy;
  onValidityChange: (isValid: boolean) => void;
};

export const PolicyAmendment = ({ details, onValidityChange }: PolicyAmendmentProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { ppi } = useContext(ProductContext);
  const coveragePerMonth: number = details.policyCalculationDetails.CoverageAmount;
  const l10n = useContext(LocalizationContext);
  const [isExpanded, expandCard] = useState(false);
  const [declarations, setDeclarations] = useState<DeclarationType[]>([]);
  const [selectedDeclaration, selectDeclaration] = useState<DeclarationType | undefined>(undefined);
  const [readDeclarations, setReadDeclarations] = useState<DeclarationType[]>([]);
  const [key, setKey] = useState(0);
  const { bankName = "", appLanguage: selectedLanguage } = useAppSelector(
    (state: RootState) => state.app
  );
  const [calculatePrice, { data: calculationData, isLoading, isSuccess }] =
    useCalculatePriceMutation();
  const [desiredPaymentFrequency, setPaymentFrequency] = useState<PaymentFrequency>("y");
  const [monthlyPrice, setMonthlyPrice] = useState<number>();
  const [monthlyGrossPrice, setMonthlyGrossPrice] = useState<number>();
  const [frequencyAdjustedPremium, setFrequencyAdjustedPremium] = useState<number>();
  const [showDeclarationCheckbox, setShowDeclarationCheckbox] = useState<boolean | undefined>(
    undefined
  );
  const [aboveIsTruthful, setAboveIsTruthful] = useState<boolean | undefined>(undefined);
  const { handleSubmit, register, reset } = useForm({
    criteriaMode: "all",
    defaultValues: { aboveIsTruthful: aboveIsTruthful ?? false },
    mode: "onChange",
    resolver: yupResolver(declarationSchema),
  });

  const showCheckboxFor = ["smokerStatus"];
  const showCancelButtonFor = ["healthDeclaration"];
  const bankNameRegEx = new RegExp("{bankName}", "g");

  useEffect(() => {
    // Reset payment frequency upon navigation to next product
    setPaymentFrequency("y");
  }, [details.productTypeId]);

  useEffect(() => {
    setDeclarations(
      details.productTypeId === ppi
        ? ["declarationOfAccession", "gtcPPI"]
        : ["smokerStatus", "healthDeclaration", "powerOfAttorney", "gtcDI"]
    );
    setReadDeclarations([]);

    expandCard(false);
    setTimeout(() => {
      expandCard(true);
    }, 550);

    const { policyDetails, policyCalculationDetails } = details;
    const desiredCoverage: number = policyDetails["DesiredCoverage"];
    const suggestions: { CoverageId: number; MonthlyPrice: number, MonthlyNetPrice: number }[] =
      policyCalculationDetails["Suggestions"];

    const coverage = suggestions.find(({ CoverageId }) => CoverageId === desiredCoverage);
    const monthlyPrice: number | undefined = coverage?.MonthlyNetPrice;

    setMonthlyPrice(monthlyPrice);
    setMonthlyGrossPrice(coverage?.MonthlyPrice ?? 0);
    setKey((prevKey) => prevKey + 1);
  }, [details]);

  useEffect(() => {
    if (declarations.length == readDeclarations.length && declarations.length > 0)
      onValidityChange(true);
  }, [readDeclarations]);

  useEffect(() => {
    if (calculationData) {
      const { policyDetails } = details;
      const desiredCoverage: number = policyDetails["DesiredCoverage"];

      const suggestions: { coverageId: number; monthlyPrice: number, monthlyNetPrice: number }[] =
        calculationData["Suggestions"];

      if (suggestions != undefined) {
        const coverage = suggestions.find(({ coverageId }) => coverageId === desiredCoverage);

        const monthlyPrice: number | undefined = coverage?.monthlyNetPrice;

        setMonthlyPrice(monthlyPrice);
        setMonthlyGrossPrice(coverage?.monthlyPrice ?? 0);
      }
    }
  }, [calculationData, isSuccess]);

  useEffect(() => {
    if (monthlyPrice) {
      dispatch(
        details.productTypeId === ppi
          ? setMonthlyPricePpi(monthlyPrice)
          : setMonthlyPriceDi(monthlyPrice)
      );

      const installmentGrossPrice = (monthlyGrossPrice ?? 0) * (desiredPaymentFrequency === "y" ? 12 : 3);
      dispatch(
        details.productTypeId === ppi
          ? setPPRates({monthlyNetPrice: monthlyPrice, monthlyGrossPrice: monthlyGrossPrice ?? 0, installmentGrossPrice: installmentGrossPrice})
          : setDIRates({monthlyNetPrice: monthlyPrice, monthlyGrossPrice: monthlyGrossPrice ?? 0, installmentGrossPrice: installmentGrossPrice})
      );

      setFrequencyAdjustedPremium(installmentGrossPrice);
    }
  }, [monthlyPrice, monthlyGrossPrice]);

  useEffect(() => {
    details.numberOfInstallments = desiredPaymentFrequency === "q" ? 4 : 1;
    details.policyDetails["Installments"] = desiredPaymentFrequency == "q" ? 4 : 1;

    const policyData: SubmitInsuranceProductRequest = {
      brokerCode: "00000000-0000-0000-0000-000000000000",
      insuranceGroupCode: "00000000-0000-0000-0000-000000000000",
      insuranceProductData: details.policyDetails,
      language: selectedLanguage ?? "de",
      productVersionCode: details.productVersionCode,
    };

    calculatePrice(policyData);
  }, [desiredPaymentFrequency]);

  const openDeclaration = (key: DeclarationType, showCheckbox: boolean = false) => {
    selectDeclaration(key);
    setShowDeclarationCheckbox(showCheckbox);
    setAboveIsTruthful(readDeclarations.includes(key) ?? false);
  };

  const closeDeclaration = (key: DeclarationType) => {
    selectDeclaration(undefined);
    setShowDeclarationCheckbox(undefined);
    setAboveIsTruthful(undefined);
    reset();
  };

  const markAsRead = (declaration: DeclarationType) => {
    setReadDeclarations((declarations) => {
      const decs = [...declarations];
      if (!decs.includes(declaration)) decs.push(declaration);

      return decs;
    });
    reset();
  };

  const confirm = () => {
    if (selectedDeclaration) {
      markAsRead(selectedDeclaration);
      selectDeclaration(undefined);
      setAboveIsTruthful(undefined);
      reset();
    }
  };

  const refuse = () => {
    navigate("/broker/info");
  };

  const handleToggleChange = (value: number | string) => {
    setPaymentFrequency((value as PaymentFrequency) || "y");
  };

  return (
    <div
      className={`mb-6 w-full flex flex-1 flex-col transition-all duration-500 ${isExpanded ? "opacity-100" : "opacity-0"}`}
    >
      <div className="card">
        <div className="card-body flex flex-col gap-6">
          <div className="grid grid-cols-1 gap-2">
            <span className="flex items-center">{l10n.label.desiredPaymentFrequency}</span>
            <div className="flex justify-center">
              <ToggleButton
                className="w-auto"
                defaultValue={"y"}
                key={key}
                options={[
                  { value: "y", text: l10n.label.yearly },
                  { value: "q", text: l10n.label.quarterly },
                ]}
                onChange={handleToggleChange}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 leading-tight">
            <span className="flex items-center">
              {details.policyCalculationDetails.CoverageFrequency == "Total"
                ? l10n.label.coverageTotal
                : l10n.label.coveragePerMonth}
            </span>
            <span className="flex justify-end items-center font-semibold">
              {Formatters.formatCurrencyNumber(coveragePerMonth, true, undefined, 0)}
            </span>

            <span className="flex items-center">{l10n.policy.policyStart}</span>
            <span className="flex justify-end items-center font-semibold">
              {Formatters.formatShortDate(details.validFromDate)}
            </span>

            <span className="flex items-center">{l10n.label.premiumPerMonth}</span>
            <span className="flex justify-end items-center font-semibold">
              {monthlyPrice ? Formatters.formatCurrencyNumber(monthlyPrice) : l10n.common.loading}
            </span>

             {desiredPaymentFrequency === "q" && (
              <div className="col-span-2 grid grid-cols-2 gap-2 leading-tight" >
               <span className="flex items-center">{l10n.label.serviceFees}</span>
               <span className="inline-flex grow justify-end font-semibold items-center">
              {frequencyAdjustedPremium
                ? Formatters.formatCurrencyNumber(20 / 3)
                : l10n.common.loading}
                 </span>
              </div>
             )}
            </div>

          {desiredPaymentFrequency === "q" && (
            <div className="notification notification-info">
              <InfoCircle className="w-[24px] h-[24px] shrink-0 fill-info" />
              <span className="text-info">{l10n.notice.yearlyDiscount}</span>
            </div>
          )}
        </div>
      </div>

      <div className="w-5/6 self-center flex flex-1 justify-between items-baseline mb-6">
        <span className="text-body text-lg leading-none">
          {desiredPaymentFrequency === "q"
            ? l10n.label.premiumPerQuarter
            : l10n.label.premiumPerYear}
        </span>
        <span className="text-xl font-semibold">
          {frequencyAdjustedPremium
            ? Formatters.formatCurrencyNumber(frequencyAdjustedPremium)
            : l10n.common.loading}
        </span>
      </div>

      <div className="card">
        <div className="card-body">
          <p className="mb-3 text-lg font-medium">{l10n.policy.additionalDocumentApprovals}</p>

          {declarations?.map((key) => (
            <div
              className="py-3 border-b border-b-card-body last:border-b-0 flex justify-between items-center"
              key={key}
              onClick={() => openDeclaration(key, showCheckboxFor.includes(key))}
            >
              <div className="w-full flex text-ellipsis">
                <DocumentText className={"w-6 h-6 shrink-0 stroke-1.5 mr-2"} />
                <div>
                  <div className="font-medium leading-none">{l10n.decls[key].name}</div>
                  <div
                    className={cn(
                      "text-sm",
                      readDeclarations.includes(key) ? "text-card" : "text-muted"
                    )}
                  >
                    {readDeclarations.includes(key)
                      ? l10n.label.confirmed
                      : l10n.label.confirmationPending}
                  </div>
                </div>
              </div>
              {readDeclarations.includes(key) ? (
                <CheckCircle className="w-6 h-6 shrink-0 stroke-card stroke-2 mr-2" />
              ) : (
                <EmptyCircle className="w-6 h-6 shrink-0 stroke-card stroke-2 mr-2" />
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedDeclaration && (
        <Modal backgroundTransparency={80} onClose={() => closeDeclaration(selectedDeclaration)}>
          <div className="modal-body">
            {selectedDeclaration === "smokerStatus" && (
              <>
                <div className="w-12 h-12 p-2 bg-primary rounded-3xl justify-center items-center inline-flex">
                  <ShieldCheck className={"w-8 h-8 stroke-primary-inverted"} />
                </div>
                <div className="flex-col justify-start items-start gap-2 flex">
                  <div className="w-72 text-center text-xl font-bold leading-7">
                    {l10n.decls[selectedDeclaration].name}
                  </div>
                </div>

                <div className="flex items-start text-body">
                  {l10n.smoker.selectedDuringTheMeetingWithAccountManager}
                </div>

                <div className="w-full inline-flex flex-row items-center">
                  {details.policyDetails?.IsSmoker === 1 ? (
                    <CheckCircle className={"w-6 h-8 stroke-2 mr-2"} />
                  ) : (
                    <EmptyCircle className={"w-6 h-8 stroke-2 mr-2"} />
                  )}
                  <div
                    className={cn(
                      "flex-1 text-base",
                      details.policyDetails?.IsSmoker === 1 && "font-semibold"
                    )}
                  >
                    {l10n.smoker.yesRegularly}
                  </div>
                </div>
                <div className="w-full inline-flex flex-row items-center">
                  {details.policyDetails?.IsSmoker === 3 ? (
                    <CheckCircle className={"w-6 h-8 stroke-2 mr-2"} />
                  ) : (
                    <EmptyCircle className={"w-6 h-8 stroke-2 mr-2"} />
                  )}
                  <div
                    className={cn(
                      "flex-1 text-base",
                      details.policyDetails?.IsSmoker === 3 && "font-semibold"
                    )}
                  >
                    {l10n.smoker.yesOccasionally}
                  </div>
                </div>
                <div className="w-full inline-flex flex-row">
                  {details.policyDetails?.IsSmoker === 2 ? (
                    <CheckCircle className={"w-6 h-8 stroke-2 mr-2"} />
                  ) : (
                    <EmptyCircle className={"w-6 h-8 stroke-2 mr-2"} />
                  )}
                  <div
                    className={cn(
                      "flex-1 text-base",
                      details.policyDetails?.IsSmoker === 2 && "font-semibold"
                    )}
                  >
                    {l10n.smoker.noIHaveNotConsumedTobaccoInLast12Months}
                  </div>
                </div>

                <div className="flex items-start text-body">
                  {l10n.smoker.thisIncludesCigarettesEtAl}
                </div>
              </>
            )}
            {selectedDeclaration !== "smokerStatus" && (
              <>
                <div className="w-12 h-12 p-2 bg-primary rounded-3xl justify-center items-center inline-flex">
                  <ShieldCheck className={"w-8 h-8 stroke-primary-inverted"} />
                </div>
                <div className="flex-col justify-start items-start gap-2 flex">
                  <div className="w-72 text-center text-xl font-bold leading-7">
                    {l10n.decls[selectedDeclaration].name}
                  </div>
                </div>

                {l10n.decls[selectedDeclaration].content.map((item, index) => (
                  <div className="w-full inline-flex flex-row" key={index}>
                    <CheckCircle className={"w-6 h-8 stroke-2 mr-2"} />
                    <div
                      className="flex-1 text-base mt-1"
                      dangerouslySetInnerHTML={{
                        __html: item.replace(bankNameRegEx, bankName),
                      }}
                    />
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="modal-actions">
            <form className="flex flex-col gap-4" onSubmit={handleSubmit(confirm)}>
              {showDeclarationCheckbox && (
                <div className="form-check">
                  <input
                    checked={aboveIsTruthful}
                    className="form-check-input"
                    id="aboveIsTruthful"
                    required
                    type="checkbox"
                    title="The above information is truthful to the best of my knowledge."
                    {...register("aboveIsTruthful")}
                    onChange={() => setAboveIsTruthful(!aboveIsTruthful)}
                  />
                  <label className="form-check-label" htmlFor="truthDeclaration">
                    {l10n.label.smokerStatusIsTruthful}
                  </label>
                </div>
              )}
              <div className="btn-group">
                <button
                  className="btn btn-primary"
                  disabled={!aboveIsTruthful && showCheckboxFor.includes(selectedDeclaration)}
                  onClick={() => confirm()}
                >
                  {l10n.btn.confirm}
                </button>
                {showCancelButtonFor.includes(selectedDeclaration) && (
                  <button className="btn btn-secondary" onClick={() => refuse()}>
                    {l10n.btn.refuse}
                  </button>
                )}
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
};
